import {
  Button,
  ButtonColorEnum,
  ChevronDownIcon,
  ChevronUpIcon,
  IconButton,
  IconButtonColorEnum,
  IconSizeEnum,
  RecIcon,
  Tooltip,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { ListboxSelected } from '@/components/Listbox';
import { TableRowImage } from '@/components/TableRowImage';
import { useBroadcastScheduleContext } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/context/BroadcastScheduleProvider';
import {
  buildNewPresenceDataToSave,
  itemHasChildren,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/helpers/batchSelectionHelpers';
import {
  INLINE_DIALOG_OPTIONS,
  PostLogoFor,
  PostPresenceFor,
  TBroadcastScheduleOnUpdateInline,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { getOffsetMs, SpritesheetThumbnail } from '@/features/video-thumbnails';
import { useThumbnailContextResolver } from '@/features/video-thumbnails/hooks/use-thumbnail-context-resolver';
import { TPresenceItem } from '@/types/presences';

export type ImageConfig = {
  url: string;
  alt: string;
  name?: string;
  contentClassName?: string;
};

export type PresenceInlineDropdownProps<T extends TPresenceItem> = {
  item: T;
  imageConfig: ImageConfig;
  renderDropdown: (onChange: (value: ListboxSelected) => void) => React.ReactNode;
  onUpdateInline?: TBroadcastScheduleOnUpdateInline<PostLogoFor<T>>;
  onPreview?: (item: T) => void;
  hasParent?: boolean;
  parentCallback?: () => void;
  buildDataToSave?: (item: T) => PostPresenceFor<T>;
  presenceData?: { id?: string };
};

export function PresenceInlineDropdown<T extends TPresenceItem>({
  item,
  imageConfig,
  renderDropdown,
  onPreview,
  onUpdateInline,
  parentCallback,
  buildDataToSave,
  hasParent = false,
  presenceData,
}: PresenceInlineDropdownProps<T>) {
  const { openInline, toggleInline, dropdownCoords, setDropdownCoordsFromRect } =
    useBroadcastScheduleContext();
  const { setDrawerLogo, prevDrawerRectRef, drawerRect, playerVideoCurrentTime } =
    useBroadcastScheduleProgram();
  const thumbnailItems = useMemo(() => [item], [item]);
  const resolveThumbnailContext = useThumbnailContextResolver(thumbnailItems);
  const { thumbnails, referenceStartDatetime } = resolveThumbnailContext(item);
  const {
    setCurrentPresenceDrawer: setCurrentLogoDrawer,
    currentPresenceDrawer: currentLogoDrawer,
  } = useBroadcastScheduleContext();

  const dataToSave = (buildDataToSave ?? buildNewPresenceDataToSave) as (
    item: T
  ) => PostPresenceFor<T>;
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const { id: itemId } = item;
  const isOpen = openInline.presence === itemId;
  const hasChildren = itemHasChildren(item);
  const { t } = useTranslation();

  useLayoutEffect(() => {
    if (isOpen && btnRef.current) {
      if (presenceData?.id !== item.id) {
        onPreview?.(item);
      }
      setDropdownCoordsFromRect(btnRef.current.getBoundingClientRect());
    }
  }, [isOpen]);

  useEffect(() => {
    if (!drawerRect || hasChildren) return;
    if (item.id !== currentLogoDrawer?.id) return;
    if (JSON.stringify(prevDrawerRectRef.current) === JSON.stringify(drawerRect)) return;

    const newData = dataToSave(item);
    prevDrawerRectRef.current = drawerRect;
    newData.parameters.bbox = drawerRect;
    onUpdateInline?.(newData, true, playerVideoCurrentTime);
    setDrawerLogo(false);
    setCurrentLogoDrawer(null);
  }, [drawerRect, item]);

  if (!itemId) return <></>;

  const handlePresenceChange = (value: ListboxSelected) => {
    const presenceSelected = value[0];
    const newData = dataToSave(item);
    const presenceId = 'logo_id' in newData ? 'logo_id' : 'people_id';
    const applySilently = false;

    newData[presenceId] = presenceSelected;
    onUpdateInline?.(newData, applySilently);
    toggleInline(INLINE_DIALOG_OPTIONS.Presence, itemId);
  };

  const handleDrawerClick = () => {
    setCurrentLogoDrawer(item);
    setDrawerLogo(prev => !prev);
  };

  return (
    <div
      className={clsx(!hasChildren && 'flex cursor-pointer items-center', 'w-full justify-between')}
    >
      {!hasChildren && (
        <>
          <Tooltip help={t('Editar cuadro delimitador')} grow>
            <IconButton color={IconButtonColorEnum.NONE_DEFAULT} onClick={handleDrawerClick}>
              <RecIcon size={IconSizeEnum.SM} />
            </IconButton>
          </Tooltip>
          <SpritesheetThumbnail
            startOffsetMs={getOffsetMs((item as any).start_datetime, referenceStartDatetime)}
            endOffsetMs={getOffsetMs((item as any).end_datetime, referenceStartDatetime)}
            thumbnails={thumbnails}
            bbox={(item as any).parameters?.bbox}
            displayWidth={100}
            alt={(item as any).entity_label ?? ''}
            presenceImageUrl={imageConfig.url}
          />
        </>
      )}

      {hasChildren && (
        <div
          className={clsx('flex min-h-[48px] items-center', hasChildren && 'ml-2')}
          onClick={() => {
            hasChildren ? parentCallback?.() : onPreview?.(item);
          }}
        >
          <TableRowImage
            contentClassName={imageConfig.contentClassName ?? 'justify-start w-[80px]'}
            className={clsx(
              'cursor-pointer',
              !hasChildren &&
                hasParent &&
                'opacity-25 transition-opacity duration-200 hover:opacity-100',
              presenceData?.id === item.id && '!opacity-100'
            )}
            source={imageConfig.url}
            alt={imageConfig.alt}
            name={imageConfig.name}
          />
        </div>
      )}

      {!hasChildren && (
        <Button
          ref={btnRef}
          color={ButtonColorEnum.SECONDARY_OLD}
          className={clsx('h-auto cursor-pointer p-0', isOpen ? 'bg-neutral-20' : 'bg-transparent')}
          onClick={() => toggleInline(INLINE_DIALOG_OPTIONS.Presence, itemId)}
          rightIcon={() => (isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />)}
        />
      )}

      {isOpen &&
        dropdownCoords &&
        createPortal(
          <div
            className="absolute z-100 mt-2 min-w-[300px] border border-neutral-20 bg-white p-4 shadow-lg"
            style={{
              top: dropdownCoords.top,
              left: dropdownCoords.right - 300,
            }}
          >
            {renderDropdown(handlePresenceChange)}
          </div>,
          document.body
        )}
    </div>
  );
}
